import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { hotTags } from '@/mocks/channels';

const navItems = [
  { label: '首页', to: '/' },
  { label: '今日热瓜', to: '/channel/regua' },
  { label: '下饭专区', to: '/channel/xiafan' },
  { label: '摸鱼段子', to: '/channel/moyu' },
  { label: '沙雕图鉴', to: '/channel/shadiaotujian' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/?q=${encodeURIComponent(value)}#stream`);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden bg-foreground-950 text-background-50 md:block">
        <div className="mx-auto flex h-9 max-w-[1400px] items-center gap-3 overflow-hidden px-6">
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-2.5 py-0.5 text-xs font-medium text-background-50">
            <i className="ri-flashlight-fill w-3.5 h-3.5 flex items-center justify-center" />
            正在热传
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee gap-6 whitespace-nowrap text-xs text-background-200">
              {[...hotTags, ...hotTags].map((tag, index) => (
                <span key={`${tag}-${index}`} className="flex items-center gap-2">
                  <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center text-primary-400" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`w-full transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 shadow-[0_1px_0_0_oklch(var(--background-200))] backdrop-blur'
            : 'bg-background-50'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-xl text-background-50">
              <i className="ri-water-flash-fill w-6 h-6 flex items-center justify-center" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl tracking-wide text-foreground-950">
                糖心吃瓜网
              </span>
              <span className="mt-1 text-[10px] tracking-[0.25em] text-foreground-500">
                TANGXIN CHIGUA
              </span>
            </span>
          </Link>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
            {navItems.map((item) => {
              const active =
                item.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                    active
                      ? 'bg-primary-100 font-medium text-primary-800'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden md:flex">
              <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100/70 px-3.5 py-2 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line w-4 h-4 flex items-center justify-center text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  placeholder="搜瓜、搜段子"
                  aria-label="站内搜索"
                  className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>

            <Link
              to="/about#contact"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 lg:inline-flex"
            >
              我要爆料
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
            >
              <i
                className={`${
                  menuOpen ? 'ri-close-line' : 'ri-menu-line'
                } w-5 h-5 flex items-center justify-center text-xl`}
              />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto max-w-[1400px] px-4 py-4 md:px-6">
              <form onSubmit={handleSearch} className="mb-3 flex md:hidden">
                <div className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-100/70 px-3.5 py-2">
                  <i className="ri-search-line w-4 h-4 flex items-center justify-center text-base text-foreground-400" />
                  <input
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    type="search"
                    placeholder="搜瓜、搜段子"
                    aria-label="站内搜索"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                </div>
              </form>
              <div className="grid grid-cols-2 gap-2">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="rounded-lg border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}